import React from 'react';
import cn from 'classnames';
import styles from './index.module.scss';

import Card from '../../Card';
import Text from '../../Text';
import Option from '../RadioOption';
import { useRadio } from '../context';

/**
 * RadioCard - A composite component that combines Radio.Option with Card
 * Renders a selectable card that acts as a radio button option
 *
 * @param {Object} props
 * @param {string} [props.id=''] - The id for the radio option
 * @param {string} [props.label=''] - The label for the radio option
 * @param {string} [props.description=''] - The description for the radio option
 * @param {string} [props.value=''] - The unique value for this radio option
 * @param {boolean} [props.disabled=false] - Whether the radio option is disabled
 * @param {string} [props.className=''] - Additional
 * CSS classes to apply to the card
 * @param {React.ReactNode} props.children - The content to display inside the card body
 * @param {string} [props.id] - The id for the radio option
 * @param {string} [props.tooltipText] - Tooltip text for the radio option
 * @param {import('utils/types').Weight} [props.labelWeight='normal'] - The font weight for the label
 * @returns {JSX.Element}
 */
export default function RadioCard({
  id = '',
  label = '',
  description = '',
  value = '',
  disabled = false,
  className = '',
  children = null,
  tooltipText = '',
  labelWeight = 'normal'
}) {
  const {
    name,
    value: selectedValue,
    disabled: isRootDisabled,
    onChange
  } = useRadio();

  const isSelected = selectedValue === value;
  const isDisabled = isRootDisabled || disabled;

  const onSelect = () => {
    if (isSelected || isDisabled) return;
    onChange(value);
  };

  return (
    <Card
      onClick={onSelect}
      data-active={isSelected}
      data-disabled={isDisabled}
      className={cn(styles.radioCard, className)}
    >
      <Card.Body>
        <Option
          id={id}
          name={name}
          label={label}
          value={value}
          disabled={disabled}
          tooltipText={tooltipText}
          labelWeight={labelWeight}
        >
          <Text variant={500} className={styles.description}>
            {description}
          </Text>
          {children}
        </Option>
      </Card.Body>
    </Card>
  );
}
